// 조직 이용 상태 안내 — 조회만이면 위 띠 · 막힘이면 전체 화면(승인 대기·반려 사유·해지 보관·복구 문구) · 막힌 고객 링크는 링크 안내.

import { ReactNode } from "react";

import { RpcError } from "@buildpy/api/client";
import {
  isOrgStatus,
  ORG_LINK_UNAVAILABLE_TEXT,
  ORG_PENDING_GUIDE_TEXT,
  ORG_REJECTED_GUIDE_TEXT,
  ORG_RETENTION_TEXT,
  orgBlockedText,
  orgReadOnlyBannerText,
  type OrgAccess,
  type OrgStatus,
} from "@buildpy/core/modules/org/status";
import Banner from "@buildpy/ui/banner";
import { Button } from "@buildpy/ui/button";
import Notice from "@buildpy/ui/notice";

const dateOf = (iso: string | null): string => (iso ? iso.slice(0, 10) : "-");

export const OrgReadOnlyBanner = ({ daysLeft, className }: { daysLeft: number; className?: string }) => (
  <Banner tone="warning" className={className}>
    {orgReadOnlyBannerText(daysLeft)}
  </Banner>
);

interface BlockedProps {
  status: OrgStatus;
  // 셸에서 왔으면 복구 기한(= 파기 예정일)까지 · api 오류에서 왔으면 상태만 안다.
  access?: Pick<OrgAccess, "restoreUntil"> | null;
  // 가입 반려(해지 + 반려 메모) — 복구 기한 대신 문의 안내.
  rejected?: boolean;
  // 운영자가 적은 반려 사유(신청자에게 보인다).
  rejectReason?: string | null;
  onLogout?: () => void;
  // 반려 재신청 칸처럼 안내 아래 붙는 것.
  children?: ReactNode;
}

export const rejectReasonLineOf = (reason: string): string => `반려 사유 · ${reason}`;

const blockedBodyOf = (status: OrgStatus, rejected: boolean): string => {
  if (status === "pending") return ORG_PENDING_GUIDE_TEXT;
  if (status === "terminated") return rejected ? ORG_REJECTED_GUIDE_TEXT : ORG_RETENTION_TEXT;
  return "결제가 확인되면 운영자가 바로 다시 열어 드립니다. 문의는 buildpy 고객센터로 주세요.";
};

export const OrgBlockedNotice = ({ status, access, rejected = false, rejectReason = null, onLogout, children }: BlockedProps) => (
  <div className="safe-top-fill flex min-h-screen items-center justify-center bg-background px-4 pb-8 pt-safe-8">
    <Notice
      className={children ? "max-w-xl" : "max-w-md"}
      title={orgBlockedText({ status, rejected })}
      body={blockedBodyOf(status, rejected)}
      actions={
        onLogout && (
          <Button size="sm" variant="outline" onClick={onLogout}>
            로그아웃
          </Button>
        )
      }
    >
      {status === "terminated" && !rejected && access && (
        <p className="mt-2 text-xs tabular-nums text-muted-foreground">
          복구 기한 {dateOf(access.restoreUntil)} · 지나면 파기
        </p>
      )}
      {status === "terminated" && rejected && rejectReason && (
        <p className="mt-3 whitespace-pre-wrap rounded-md bg-muted p-3 text-left text-sm" data-testid="org-reject-reason">
          {rejectReasonLineOf(rejectReason)}
        </p>
      )}
      {children}
    </Notice>
  </div>
);

// 공개 페이지(시공사례·홈페이지 링크·간편접수)는 제목을 ORG_PAGE_UNAVAILABLE_TEXT 로 바꿔 쓴다.
export const OrgLinkUnavailableNotice = ({ title = ORG_LINK_UNAVAILABLE_TEXT }: { title?: string }) => (
  <div className="flex min-h-screen items-center justify-center bg-background px-4">
    <Notice title={title} body="시공사에 직접 문의해 주세요." />
  </div>
);

export type OrgAccessFailure = { kind: "blocked"; status: OrgStatus; rejected: boolean; rejectReason: string | null } | { kind: "unavailable" } | { kind: "read_only"; message: string };

// 막힘 상태 — 정상(active)·모르는 값은 막힘이 아니므로 중지로 본다(fail-closed).
const blockedStatusOf = (raw: unknown): OrgStatus => (isOrgStatus(raw) && raw !== "active" ? raw : "suspended");

// api 가 돌려준 403 org_* 를 갈라낸다 — 그 밖의 오류는 null.
export const orgAccessFailureOf = (error: unknown): OrgAccessFailure | null => {
  if (!(error instanceof RpcError) || error.status !== 403) return null;
  const body = (error.body ?? {}) as { error?: unknown; status?: unknown; rejected?: unknown; rejectReason?: unknown };
  if (body.error === "org_unavailable") return { kind: "unavailable" };
  if (body.error === "org_read_only") return { kind: "read_only", message: error.message };
  if (body.error === "org_blocked") {
    const rejected = body.rejected === true;
    const reason = rejected && typeof body.rejectReason === "string" && body.rejectReason.trim() ? body.rejectReason : null;
    return { kind: "blocked", status: blockedStatusOf(body.status), rejected, rejectReason: reason };
  }
  return null;
};
